import { Command } from 'commander';

import { graphqlRaw } from '../graphql/client.ts';
import { clearApplicationSession, saveApplicationSession } from '../lib/application-session.ts';
import { applicationIdentity, fetchClientCredentialsToken } from '../lib/client-credentials.ts';
import { error, hint, info, success } from '../lib/output.ts';

const CREATE = `
  mutation UseApplication($applicationId: ID!, $name: String!) {
    createApplicationCredential(input: { applicationId: $applicationId, name: $name, session: true }) {
      clientId
      clientSecret
      credential { id }
    }
  }
`;

const REVOKE = `
  mutation ReleaseSessionCredential($credentialId: ID!) {
    revokeApplicationCredential(input: { credentialId: $credentialId }) { revokedAt }
  }
`;

interface IssuedSessionCredential {
  clientId: string;
  clientSecret: string;
  credential: { id: string };
}

export const useCommand = new Command('use')
  .summary('Act as an application, or go back to acting as yourself')
  .description(
    'Act as an application, or go back to acting as yourself\n\n' +
      'Examples:\n' +
      '  sl use application <id>     Act as that application for one token lifetime\n' +
      '  sl use me                   Go back to acting as yourself',
  );

useCommand
  .command('application <id>')
  .description('Act as an application for one token lifetime')
  .action(async function (this: Command, id: string) {
    const opts = this.optsWithGlobals();

    // The credential exists only long enough to be exchanged. Revoking it at once
    // means nothing reusable is left anywhere: what lands on disk is a bearer
    // token that expires, and a subgraph verifies it by signature and `exp`
    // against the JWKS, never by asking Hermes whether the credential still lives.
    // asPerson: creating and revoking a credential is a management operation,
    // which Hermes refuses to an application token — so switching straight from
    // one application to another has to authenticate as the person regardless of
    // what is currently active.
    const asPerson = { endpoint: opts.endpoint, throwOnError: true, asPerson: true };

    let credential: IssuedSessionCredential | null = null;
    try {
      const issued = (await graphqlRaw(CREATE, { applicationId: id, name: sessionName() }, asPerson)) as {
        createApplicationCredential: IssuedSessionCredential | null;
      };
      credential = issued.createApplicationCredential;
    } catch (err) {
      error(`Could not create a session credential: ${(err as Error).message}`);
      hint('`sl applications` lists the applications you can manage.');
      process.exit(1);
    }

    // A refused mutation can arrive as data carrying a null field rather than as
    // a thrown error, so catching is not enough on its own.
    if (!credential) {
      error('The server did not return a session credential.');
      hint('`sl applications` lists the applications you can manage.');
      process.exit(1);
    }

    let accessToken: string | null = null;
    try {
      accessToken = await fetchClientCredentialsToken({
        clientId: credential.clientId,
        clientSecret: credential.clientSecret,
      });
    } catch (err) {
      error(`Could not exchange the credential for a token: ${(err as Error).message}`);
      hint('`sl config use-local` / `use-staging` / `use-production` set the token endpoint.');
    } finally {
      // Revoked either way: a credential whose exchange failed must not survive.
      await graphqlRaw(REVOKE, { credentialId: credential.credential.id }, asPerson);
    }

    if (!accessToken) process.exit(2);

    const identity = applicationIdentity(accessToken);
    if (!identity) {
      error('The token endpoint returned something this CLI could not read.');
      process.exit(1);
    }

    saveApplicationSession({
      accessToken,
      applicationId: identity.applicationId,
      expiresAt: identity.expiresAt.getTime(),
    });

    success(`Now acting as application ${identity.applicationId}`);
    info(`  until ${identity.expiresAt.toISOString()}`);
    hint('The credential behind this token is already revoked, so nothing reusable is stored.');
    hint('Managing companies, applications and credentials is now out of reach: Hermes refuses');
    hint('its own tokens on its management API. Run `sl use me` to act as yourself again.');
  });

useCommand
  .command('me')
  .description('Go back to acting as yourself')
  .action(() => {
    if (clearApplicationSession()) {
      success('Now acting as yourself again.');
      return;
    }
    info('Already acting as yourself.');
  });

function sessionName(): string {
  return `cli-session-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}`;
}
